import React, { useState, useEffect } from 'react';
import { useUser } from '../UserContext';
import { getAuth, updateProfile, updateEmail, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/free-solid-svg-icons';

const ProfilePage = () => {
  const { user, updateUserProfile } = useUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoURL, setPhotoURL] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    console.log('ProfilePage useEffect triggered');
    if (user) {
      setName(user.displayName || '');
      setEmail(user.email || '');
      setPhotoURL(user.photoURL || '');
      setAddress(user.address || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const storage = getStorage();

    try {
      const updatedData = { displayName: name, address };

      if (email !== user.email) {
        await updateEmail(auth.currentUser, email);
        updatedData.email = email;
      }

      if (photo) {
        const photoRef = ref(storage, `profilePhotos/${user.uid}`);
        await uploadBytes(photoRef, photo);
        const newPhotoURL = await getDownloadURL(photoRef);
        await updateProfile(auth.currentUser, { photoURL: newPhotoURL });
        updatedData.photoURL = newPhotoURL;
        setPhotoURL(newPhotoURL);
      }

      await updateUserProfile(updatedData);

      setMessage('Profile updated successfully!');
      setMessageType('success');
    } catch (error) {
      setMessage(`Error updating profile: ${error.message}`);
      setMessageType('error');
    }
  };

  const handlePasswordReset = async () => {
    try {
      await sendPasswordResetEmail(getAuth(), email);
      setMessage('Password reset email sent. Check your inbox.');
      setMessageType('success');
    } catch (error) {
      setMessage(`Error sending password reset email: ${error.message}`);
      setMessageType('error');
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files[0]) {
      setPhoto(e.target.files[0]);
    }
  };

  if (!user) return <div>Please log in to view this page.</div>;

  return (
    <div className="container max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Profile</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block mb-1 font-medium">Name:</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label htmlFor="email" className="block mb-1 font-medium">Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label htmlFor="address" className="block mb-1 font-medium">Address:</label>
          <textarea
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label htmlFor="photo" className="block mb-1 font-medium">Profile Photo:</label>
          <input
            type="file"
            id="photo"
            onChange={handlePhotoChange}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {photoURL && (
          <div>
            <img src={photoURL} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
          </div>
        )}
        <div>
          <button
            type="button"
            onClick={handlePasswordReset}
            className="text-blue-500 hover:text-blue-700 focus:outline-none"
          >
            <FontAwesomeIcon icon={faKey} className="mr-2" />
            Reset Password
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            Update Profile
          </button>
        </div>
      </form>
      {message && (
        <p className={`mt-4 p-2 rounded ${
          messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default ProfilePage;
